import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { apiService } from '../services/apiService';
import notificationService from '../CommanService/notificationService';
import signalRService from '../services/signalRService';
import './BatchPricingPage.css';

const BatchPricingPage = () => {
  const { user } = useSelector(state => state.auth);
  const [jobStatus, setJobStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [results, setResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);

  // Load job status on component mount
  useEffect(() => {
    loadJobStatus();
    
    // Set up SignalR listener for real-time updates
    const handleJobStatusChange = (event) => {
      const newJobStatus = event.detail;
      setJobStatus(newJobStatus);
      console.log('Job status updated via SignalR:', newJobStatus);
    };

    window.addEventListener('jobStatusChanged', handleJobStatusChange);

    return () => {
      window.removeEventListener('jobStatusChanged', handleJobStatusChange);
    };
  }, []);

  // Load results when job is completed
  useEffect(() => {
    if (jobStatus?.status === 'ready_to_run' || jobStatus?.status === 'completed') {
      loadResults();
    }
  }, [jobStatus?.status, currentPage]);

  const loadJobStatus = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.GetJobStatus();
      if (response && response.data) {
        setJobStatus(response.data);
      }
    } catch (error) {
      console.error('Error loading job status:', error);
      notificationService.showError('Failed to load job status');
    } finally {
      setIsLoading(false);
    }
  };

  const loadResults = async () => {
    try {
      const response = await apiService.GetBatchPricingResults(currentPage, pageSize);
      if (response && response.data) {
        setResults(response.data);
        // Calculate total pages based on response
        // Note: You might need to modify the API to return total count
        setTotalPages(Math.ceil(response.data.length / pageSize));
      }
    } catch (error) {
      console.error('Error loading results:', error);
      notificationService.showError('Failed to load batch pricing results');
    }
  };

  const handleStartBatchPricing = async () => {
    try {
      setIsStarting(true);
      const response = await apiService.StartBatchPricing();
      
      if (response && response.data) {
        notificationService.showSuccess('Batch pricing job started successfully');
        await loadJobStatus(); // Refresh status
      } else {
        notificationService.showError(response?.message || 'Failed to start batch pricing job');
      }
    } catch (error) {
      console.error('Error starting batch pricing:', error);
      notificationService.showError('Failed to start batch pricing job');
    } finally {
      setIsStarting(false);
    }
  };

  const handleResetJob = async () => {
    if (!window.confirm('Are you sure you want to reset the batch pricing job? This will clear all finalized data.')) {
      return;
    }

    try {
      setIsResetting(true);
      const response = await apiService.ResetJobStatus();
      
      if (response && response.data) {
        notificationService.showSuccess('Job status reset successfully');
        setJobStatus(null);
        setResults([]);
        await loadJobStatus(); // Refresh status
      } else {
        notificationService.showError(response?.message || 'Failed to reset job status');
      }
    } catch (error) {
      console.error('Error resetting job status:', error);
      notificationService.showError('Failed to reset job status');
    } finally {
      setIsResetting(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString();
    } catch {
      return dateTimeString;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'running':
        return 'badge bg-warning text-dark';
      case 'ready_to_run':
        return 'badge bg-success';
      case 'failed':
        return 'badge bg-danger';
      case 'completed':
        return 'badge bg-primary';
      default:
        return 'badge bg-secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'running':
        return 'Running';
      case 'ready_to_run':
        return 'Ready to Run';
      case 'failed':
        return 'Failed';
      case 'completed':
        return 'Completed';
      default:
        return 'Unknown';
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Batch Pricing Management</h4>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-warning"
                  onClick={handleStartBatchPricing}
                  disabled={isStarting || jobStatus?.status === 'running'}
                >
                  {isStarting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Starting...
                    </>
                  ) : (
                    'Start Batch Pricing'
                  )}
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleResetJob}
                  disabled={isResetting || jobStatus?.status === 'running'}
                >
                  {isResetting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Resetting...
                    </>
                  ) : (
                    'Reset Job'
                  )}
                </button>
              </div>
            </div>

            <div className="card-body">
              {/* Job Status Section */}
              <div className="row mb-4">
                <div className="col-12">
                  <h5>Job Status</h5>
                  {jobStatus ? (
                    <div className="card">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-3">
                            <strong>Status:</strong>
                            <span className={`ms-2 ${getStatusBadgeClass(jobStatus.status)}`}>
                              {getStatusText(jobStatus.status)}
                            </span>
                          </div>
                          <div className="col-md-3">
                            <strong>Progress:</strong>
                            <span className="ms-2">
                              {jobStatus.processedCombinations || 0} / {jobStatus.totalCombinations || 0}
                              ({jobStatus.progressPercentage || 0}%)
                            </span>
                          </div>
                          <div className="col-md-3">
                            <strong>Started At:</strong>
                            <span className="ms-2">{formatDateTime(jobStatus.startedAt)}</span>
                          </div>
                          <div className="col-md-3">
                            <strong>Completed At:</strong>
                            <span className="ms-2">{formatDateTime(jobStatus.completedAt)}</span>
                          </div>
                        </div>
                        {jobStatus.currentUserName && (
                          <div className="row mt-2">
                            <div className="col-12">
                              <strong>Current User:</strong>
                              <span className="ms-2">{jobStatus.currentUserName}</span>
                            </div>
                          </div>
                        )}
                        {jobStatus.errorMessage && (
                          <div className="row mt-2">
                            <div className="col-12">
                              <div className="alert alert-danger">
                                <strong>Error:</strong> {jobStatus.errorMessage}
                              </div>
                            </div>
                          </div>
                        )}
                        {jobStatus.status === 'running' && (
                          <div className="row mt-2">
                            <div className="col-12">
                              <div className="progress">
                                <div 
                                  className="progress-bar progress-bar-striped progress-bar-animated" 
                                  role="progressbar" 
                                  style={{ width: `${jobStatus.progressPercentage || 0}%` }}
                                  aria-valuenow={jobStatus.progressPercentage || 0}
                                  aria-valuemin="0" 
                                  aria-valuemax="100"
                                >
                                  {jobStatus.progressPercentage || 0}%
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-info">
                      No job status available. Click "Start Batch Pricing" to begin.
                    </div>
                  )}
                </div>
              </div>

              {/* Results Section */}
              {(jobStatus?.status === 'ready_to_run' || jobStatus?.status === 'completed') && (
                <div className="row">
                  <div className="col-12">
                    <h5>Batch Pricing Results</h5>
                    {results.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-striped table-hover">
                          <thead className="table-dark">
                            <tr>
                              <th>Part Name</th>
                              <th>Region</th>
                              <th>IC Number</th>
                              <th>Mileage</th>
                              <th>Part Price</th>
                              <th>Adjusted Price</th>
                              <th>Created On</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.map((item, index) => (
                              <tr key={index}>
                                <td>{item.partName}</td>
                                <td>{item.region}</td>
                                <td>{item.interchangeNumber}</td>
                                <td>{formatNumber(item.mileage)}</td>
                                <td>${formatNumber(item.partPrice || 0)}</td>
                                <td>${formatNumber(item.adjustedPrice)}</td>
                                <td>{formatDateTime(item.createdOn)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                          <nav aria-label="Results pagination">
                            <ul className="pagination justify-content-center">
                              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button 
                                  className="page-link" 
                                  onClick={() => setCurrentPage(currentPage - 1)}
                                  disabled={currentPage === 1}
                                >
                                  Previous
                                </button>
                              </li>
                              {[...Array(totalPages)].map((_, i) => (
                                <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => setCurrentPage(i + 1)}
                                  >
                                    {i + 1}
                                  </button>
                                </li>
                              ))}
                              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button 
                                  className="page-link" 
                                  onClick={() => setCurrentPage(currentPage + 1)}
                                  disabled={currentPage === totalPages}
                                >
                                  Next
                                </button>
                              </li>
                            </ul>
                          </nav>
                        )}
                      </div>
                    ) : (
                      <div className="alert alert-info">
                        No results available. Start a batch pricing job to see results.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchPricingPage;
